import {App, Button, Divider, Flex, Modal, Spin} from "antd";
import React, {useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from "react";
import {
    IAdmissionStatement,
    IEditProfilesRequest,
    IEditProfilesRequestData,
    IEditProfilesTraining,
} from "../../model/admissionModel";
import "./EditProfiles.less";
import {getRequestWithParams, postRequest} from "../../../../api/httpRequest";
import {useLocation, useNavigate} from "react-router";
import Typography from "antd/lib/typography";
import {FaLongArrowAltDown, FaLongArrowAltUp} from "react-icons/fa";
import useBreakpoint from "antd/es/grid/hooks/useBreakpoint";
import {LeftOutlined} from "@ant-design/icons";

type SwapDirection = "up" | "down";

function isPaidAdmissionBasis(basisTraining: string | undefined): boolean {
    const basis = (basisTraining ?? "").toLowerCase();
    return basis.includes("места по договорам") || basis.includes("договор") || basis.includes("платная");
}

/** Заголовок страницы редактирования приоритетов профилей по уровню образования и основе обучения. */
function getEditProfilesTitle(statement: IAdmissionStatement | undefined, kindsLevelFormationText: string): string {
    if (!statement) {
        return "Редактирование приоритетов профилей";
    }
    const paid = isPaidAdmissionBasis(statement.BasisTraining);
    const formation = kindsLevelFormationText;
    if (formation.includes("Специализированное высшее образование")) {
        return paid
            ? "Редактирование приоритетов профилей при поступлении по программам магистратуры (на места по договорам)"
            : "Редактирование приоритетов профилей при поступлении по программам магистратуры (на бюджет)";
    }
    if (formation.includes("Базовое высшее образование")) {
        return paid
            ? "Редактирование приоритетов профилей при поступлении по программам высшего образования (на места по договорам)"
            : "Редактирование приоритетов профилей при поступлении по программам высшего образования (на бюджет)";
    }
    return `Редактирование профилей по заявлению №${statement.Number} ${statement.Statement}`;
}

function normalizeProfilePriorities(profiles: IEditProfilesRequestData[]): IEditProfilesRequestData[] {
    return profiles.map((p, i) => ({...p, ProfilePriority: i + 1}));
}

function trainingProfileOrderKey(training: IEditProfilesTraining[]): string {
    return JSON.stringify(
        training.map((g) => ({
            g: g.CompetitionGroupCode,
            p: (g.Profiles ?? []).map((x) => x.ProfileCode),
        })),
    );
}

function buildInitialProfilesByGroup(training: IEditProfilesTraining[]): Map<string, string[]> {
    const map = new Map<string, string[]>();
    for (const g of training) {
        map.set(g.CompetitionGroupCode, (g.Profiles ?? []).map((p) => p.ProfileCode));
    }
    return map;
}

/** Профили, чья позиция в группе отличается от загруженной с сервера. */
function changedProfileCodesInGroup(
    groupCode: string,
    profiles: IEditProfilesRequestData[],
    initialByGroup: Map<string, string[]> | null,
): Set<string> {
    if (!initialByGroup) {
        return new Set();
    }
    const initial = initialByGroup.get(groupCode);
    if (!initial) {
        return new Set();
    }
    const current = profiles.map((p) => p.ProfileCode);
    if (initial.length !== current.length) {
        return new Set(current);
    }
    const changed = new Set<string>();
    current.forEach((code, i) => {
        if (initial.indexOf(code) !== i) {
            changed.add(code);
        }
    });
    return changed;
}

const ConcourseCard: React.FC<{
    concourseGroup: IEditProfilesTraining;
    xl: boolean;
    cardIndex: number;
    swapProfiles: (groupCode: string, profileCode: string, direction: SwapDirection) => void;
    changedProfileCodes: ReadonlySet<string>;
}> = ({concourseGroup, xl, cardIndex, swapProfiles, changedProfileCodes}) => {
    const listRef = useRef<HTMLDivElement>(null);
    const beforeRectsRef = useRef<Map<string, DOMRect>>(new Map());

    const profiles = concourseGroup.Profiles ?? [];

    useLayoutEffect(() => {
        const container = listRef.current;
        if (!container) {
            return;
        }
        const nodes = container.querySelectorAll("[data-profile-code]");
        nodes.forEach((node) => {
            const el = node as HTMLElement;
            const code = el.dataset.profileCode;
            if (!code) {
                return;
            }
            const old = beforeRectsRef.current.get(code);
            if (!old) {
                return;
            }
            const next = el.getBoundingClientRect();
            const dy = old.top - next.top;
            beforeRectsRef.current.delete(code);
            if (Math.abs(dy) < 0.5) {
                return;
            }
            el.style.transform = `translateY(${dy}px)`;
            el.style.transition = "none";
            requestAnimationFrame(() => {
                el.style.transition = "transform 0.28s cubic-bezier(0.4, 0, 0.2, 1)";
                el.style.transform = "";
            });
        });
    }, [profiles]);

    const handleProfileSwap = (profileCode: string, direction: SwapDirection) => {
        const container = listRef.current;
        if (container) {
            container.querySelectorAll("[data-profile-code]").forEach((node) => {
                const el = node as HTMLElement;
                const code = el.dataset.profileCode;
                if (code) {
                    beforeRectsRef.current.set(code, el.getBoundingClientRect());
                }
            });
        }
        swapProfiles(concourseGroup.CompetitionGroupCode, profileCode, direction);
    };

    const cardNo = cardIndex + 1;

    return (
        <Flex className={xl ? "admission-speciality-card" : "admission-speciality-card-mobile"} vertical={true}
              justify={"space-between"}>
            <Flex vertical className={"edit-concourse-card-header"} style={{width: "100%", backgroundColor: "whitesmoke"}}>
                <Flex
                    align={"stretch"}
                    gap={12}
                    justify={"flex-start"}
                    wrap={false}
                    className={"edit-concourse-card-header-row"}
                >
                    <Flex className={"edit-concourse-card-priority-tall"} align={"center"} justify={"center"}>
                        {cardNo}
                    </Flex>
                    <div className={"edit-concourse-card-title-col"}>
                        <Typography.Text strong className={"edit-concourse-card-title-text"}>
                            {concourseGroup.specQualifier} {concourseGroup.specName}
                        </Typography.Text>
                        <Typography.Text className={"edit-concourse-meta-line edit-concourse-meta-line--inline"}>
                            Форма обучения: {concourseGroup.kindsTrainingdisplay}
                        </Typography.Text>
                        <Typography.Text className={"edit-concourse-meta-line edit-concourse-meta-line--inline"}>
                            Основа обучения: {concourseGroup.basistraining}
                        </Typography.Text>
                    </div>
                </Flex>
            </Flex>
            {profiles.length > 0 && (
                <>
                    <div style={{backgroundColor: "lightgray", width: "100%", height: "1px"}}/>
                    <Flex
                        ref={listRef}
                        className={"edit-profiles-list"}
                        style={{padding: "8px 16px"}}
                        vertical={true}
                        justify={"center"}
                        align={"stretch"}
                        gap={8}>
                        {profiles.map((profile, index) => {
                            const rowChanged = changedProfileCodes.has(profile.ProfileCode);
                            return (
                            <div
                                key={profile.ProfileCode}
                                className={`edit-profiles-row-wrap${rowChanged ? " edit-profiles-row-wrap--changed" : ""}`}
                                data-profile-code={profile.ProfileCode}
                                title={rowChanged ? "Порядок этого профиля изменён относительно сохранённого" : undefined}
                            >
                                <Flex
                                    className={"edit-profiles-row"}
                                    justify={"space-between"}
                                    align={"center"}
                                    gap={12}
                                    wrap={false}
                                >
                                    <Flex className={"edit-profiles-priority edit-profiles-priority-compound"} align={"center"} justify={"center"}>
                                        {`${cardNo}.${index + 1}`}
                                    </Flex>
                                    <Flex align="center" className={"edit-profiles-name-cell"} style={{flex: 1, minWidth: 0}} wrap={false}>
                                        <div className={"edit-profiles-name"}>{profile.ProfileName}</div>
                                        <Typography.Text
                                            className={`edit-profiles-changed-pill${rowChanged ? " edit-profiles-changed-pill--visible" : ""}`}
                                            aria-hidden={!rowChanged}
                                        >Изменено</Typography.Text>
                                    </Flex>
                                    <Flex align={"center"} gap={8} className={"edit-profiles-arrows"}>
                                        <Flex vertical gap={4}>
                                            <Button
                                                type={"text"}
                                                className={"edit-profiles-arrow-btn edit-profiles-arrow-btn--up"}
                                                aria-label={"Переместить выше"}
                                                icon={<FaLongArrowAltUp size={22}/>}
                                                onClick={() => handleProfileSwap(profile.ProfileCode, "up")}
                                                disabled={index === 0}
                                                style={index === 0 ? {visibility: 'hidden'} : {}}
                                            />
                                        </Flex>
                                        <Flex vertical gap={4}>
                                            <Button
                                                type={"text"}
                                                className={"edit-profiles-arrow-btn edit-profiles-arrow-btn--down"}
                                                aria-label={"Переместить ниже"}
                                                icon={<FaLongArrowAltDown size={22}/>}
                                                onClick={() => handleProfileSwap(profile.ProfileCode, "down")}
                                                disabled={index === profiles.length - 1}
                                                style={index === profiles.length - 1 ? {visibility: 'hidden'} : {}}
                                            />
                                        </Flex>
                                    </Flex>
                                </Flex>
                            </div>
                            );
                        })}
                    </Flex>
                </>
            )}
        </Flex>
    );
};

export default function EditProfiles() {

    const [editProfiles, setProfilesEdit] = useState<IEditProfilesRequest>({
        training: [],
        UID: "",
    });
    const [sending, setSending] = useState<boolean>(false);
    const [loading, setLoading] = useState(true);
    const [isOverflowing, setIsOverflowing] = useState(false);
    const location = useLocation();
    const {notification} = App.useApp();
    const navigate = useNavigate();
    const {xl} = useBreakpoint();
    const initialOrderKeyRef = useRef<string | null>(null);
    const initialProfilesByGroupRef = useRef<Map<string, string[]> | null>(null);
    const pageRef = useRef<HTMLDivElement>(null);

    useLayoutEffect(() => {
        const pageEl = pageRef.current;
        if (!pageEl) return;

        const check = () => {
            const barEl = pageEl.querySelector<HTMLElement>('.edit-profiles-save-bar');
            if (!barEl) return;
            const isFixed = barEl.classList.contains('edit-profiles-save-bar--fixed');
            const barHeight = barEl.offsetHeight;
            const baseHeight = pageEl.scrollHeight - (isFixed ? 0 : barHeight);
            setIsOverflowing(baseHeight > window.innerHeight);
        };

        check();

        const ro = new ResizeObserver(check);
        ro.observe(pageEl);
        window.addEventListener('resize', check);

        return () => {
            ro.disconnect();
            window.removeEventListener('resize', check);
        };
    }, []);

    const statement = location.state?.statement as IAdmissionStatement | undefined;
    const kindsLevelFormationText = useMemo(() => {
        if (!loading && editProfiles.training[0]?.kindsLevelFormation) {
            return editProfiles.training[0].kindsLevelFormation;
        }
        return statement?.training?.[0]?.kindsLevelFormation ?? "";
    }, [loading, editProfiles.training, statement?.training]);

    const pageTitle = useMemo(
        () => getEditProfilesTitle(statement, kindsLevelFormationText),
        [statement, kindsLevelFormationText],
    );

    const isDirty = useMemo(() => {
        if (loading || initialOrderKeyRef.current === null) {
            return false;
        }
        return trainingProfileOrderKey(editProfiles.training) !== initialOrderKeyRef.current;
    }, [editProfiles.training, loading]);

    const navigateToAdmissionList = useCallback(() => {
        navigate("/admission");
    }, [navigate]);

    const confirmLeaveIfDirty = useCallback(
        (onConfirm: () => void) => {
            if (!isDirty) {
                onConfirm();
                return;
            }
            Modal.confirm({
                title: "Несохранённые изменения",
                content: "Если вы покинете страницу, внесённые изменения не будут сохранены.",
                okText: "Покинуть без сохранения",
                cancelText: "Остаться",
                centered: true,
                onOk: onConfirm,
            });
        },
        [isDirty],
    );

    const handleBackToList = useCallback(() => {
        confirmLeaveIfDirty(navigateToAdmissionList);
    }, [confirmLeaveIfDirty, navigateToAdmissionList]);

    useEffect(() => {
        getRequestWithParams(`/server/proxy/contracts/GetApplication`, {docID: location.state.statement.UID})
            .then((res) => {
                const data: IEditProfilesRequest = res.data;
                const sortedGroups = [...(data.training ?? [])].sort(
                    (a, b) => a.CompetitionGroupPriority - b.CompetitionGroupPriority,
                );
                const training = sortedGroups.map((g, gi) => {
                    const sortedProfiles = [...(g.Profiles ?? [])].sort((a, b) => a.ProfilePriority - b.ProfilePriority);
                    return {
                        ...g,
                        CompetitionGroupPriority: gi + 1,
                        Profiles: normalizeProfilePriorities(sortedProfiles),
                    };
                });
                initialOrderKeyRef.current = trainingProfileOrderKey(training);
                initialProfilesByGroupRef.current = buildInitialProfilesByGroup(training);
                setProfilesEdit({...data, training});
                setLoading(false);
            });
    }, []);

    const swapProfiles = useCallback((groupCode: string, profileCode: string, direction: SwapDirection) => {
        setProfilesEdit((prev) => ({
            ...prev,
            training: prev.training.map((g) => {
                if (g.CompetitionGroupCode !== groupCode) {
                    return g;
                }
                const list = [...(g.Profiles ?? [])];
                const i = list.findIndex((p) => p.ProfileCode === profileCode);
                if (i < 0) {
                    return g;
                }
                const j = direction === "up" ? i - 1 : i + 1;
                if (j < 0 || j >= list.length) {
                    return g;
                }
                [list[i], list[j]] = [list[j], list[i]];
                return {...g, Profiles: normalizeProfilePriorities(list)};
            }),
        }));
    }, []);

    const handleSave = useCallback(() => {
        if (loading || !isDirty) {
            return;
        }
        setSending(true);
        const sorted = [...editProfiles.training]
            .sort((a, b) => a.CompetitionGroupPriority - b.CompetitionGroupPriority)
            .map((group) => ({
                ...group,
                Profiles: [...(group.Profiles ?? [])].sort((a, b) => a.ProfilePriority - b.ProfilePriority),
            }));
        postRequest("/server/proxy/contracts/ChangePriority", {
            ...editProfiles,
            training: sorted,
        })
            .then(() => {
                setSending(false);
                initialOrderKeyRef.current = trainingProfileOrderKey(sorted);
                notification.success({
                    message: `Приоритеты профилей успешно изменены`,
                    duration: 3,
                });
                navigate("/admission");
            })
            .catch((error) => {
                setSending(false);
                notification.error({
                    message: `${JSON.stringify(error)}`,
                    duration: 3,
                });
            });
    }, [editProfiles, isDirty, loading, navigate, notification]);

    return (
        <Flex ref={pageRef} className={`edit-profiles-page${isDirty ? " edit-profiles-page--pending-save" : ""}`} vertical={true}>
            <Flex vertical={true} gap={12} style={{width: "100%", paddingTop: 16}}>
                <Button icon={<LeftOutlined/>} onClick={handleBackToList} style={{alignSelf: "flex-start"}}>
                    Вернуться к списку заявлений
                </Button>
                <Flex
                    justify={"center"}
                    align={"center"}
                    style={{
                        fontSize: 18,
                        fontWeight: "bold",
                        textAlign: "center",
                        width: "100%",
                    }}
                >{pageTitle}</Flex>
            </Flex>
            <Divider style={{margin: "12px 0"}}/>
            <div className={"edit-profiles-main"}>
                {loading ?
                    <Flex justify={"center"} align={"center"} className={"edit-profiles-spin-wrap"}>
                        <Spin/>
                    </Flex> :
                    <div className={"edit-concourse-cards-root"}>
                        <Flex vertical gap={16} style={{width: "100%"}}>
                            {editProfiles.training.map((training, cardIndex) => {
                                const changedInGroup = changedProfileCodesInGroup(
                                    training.CompetitionGroupCode,
                                    training.Profiles ?? [],
                                    initialProfilesByGroupRef.current,
                                );
                                return (
                                <div
                                    key={training.CompetitionGroupCode}
                                    className={"edit-concourse-card-outer"}
                                >
                                    <ConcourseCard
                                        concourseGroup={training}
                                        xl={!!xl}
                                        cardIndex={cardIndex}
                                        swapProfiles={swapProfiles}
                                        changedProfileCodes={changedInGroup}
                                    />
                                </div>
                                );
                            })}
                        </Flex>
                    </div>}
            </div>
            <div className={`edit-profiles-save-bar${isDirty ? (isOverflowing ? " edit-profiles-save-bar--fixed" : " edit-profiles-save-bar--sticky") : ""}`}>
                <Flex justify={"center"} style={{width: "100%"}}>
                    <Button
                        className={"edit-profiles-save-btn"}
                        loading={sending}
                        disabled={loading || !isDirty}
                        type={"primary"}
                        onClick={handleSave}
                    >Сохранить изменение приоритета профилей</Button>
                </Flex>
            </div>
        </Flex>
    );
}
